import React, { FC } from 'react';
import TwField, { TwInputTypes, TwFieldProps } from '../TwFields/TwFields';
import Box from '@mui/material/Box';
import TwTooltip from '../TwTooltip/TwTooltip';
import { styled } from '@mui/material/styles';
import { ReactComponent as WarningSign } from '../../assets/warning-yellow.svg';

const CounterBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px',
  color: '#6B7280',
  fontSize: '12px'
});

interface TwExpandableTextareaProps extends Omit<TwFieldProps, 'inputType' | 'onInputChange'> {
  /**
   * Maximum number of characters allowed
   */
  maxCharacters?: number;
  /**
   * Threshold value to show warning
   */
  thresholdCount?: number;
  /**
   * Warning message to show in tooltip
   */
  warningMessage?: string;
  /**
   * Input change handler
   */
  onInputChange: (
    event: React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLInputElement>
  ) => void;
}

const TwExpandableTextarea: FC<TwExpandableTextareaProps> = ({
  maxCharacters,
  thresholdCount,
  warningMessage = 'Approaching character limit',
  value = '',
  onInputChange,
  ...props
}) => {
  const currentLength = String(value).length;
  const showWarning = thresholdCount !== undefined && currentLength >= thresholdCount;

  const handleInputChange = (
    event: React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (maxCharacters === undefined || event.target.value.length <= maxCharacters) {
      onInputChange(event);
    }
  };

  return (
    <Box>
      <TwField
        inputType={TwInputTypes.textarea}
        value={value}
        sx={{ '& textarea': { marginBottom: '0px' } }}
        onInputChange={handleInputChange}
        {...props}
      />
      {maxCharacters !== undefined && (
        <CounterBox>
          {showWarning && (
            <TwTooltip title={warningMessage}>
              <WarningSign width={16} height={16} />
            </TwTooltip>
          )}
          <span>
            {currentLength}/{maxCharacters}
          </span>
        </CounterBox>
      )}
    </Box>
  );
};

export default TwExpandableTextarea;
